import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  AlertTriangle, 
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Alerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [clients, setClients] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [alertsRes, clientsRes, labsRes] = await Promise.all([
        axios.get(`${API}/alerts`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/labs`)
      ]);
      setAlerts(alertsRes.data);
      setClients(clientsRes.data);
      setLabs(labsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (alertId, status) => {
    try {
      await axios.put(`${API}/alerts/${alertId}`, { status });
      toast.success(status === 'resolved' ? 'Alerte résolue' : 'Alerte mise à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.client_name || 'Inconnu';
  };

  const getLabName = (labId) => {
    const lab = labs.find(l => l.id === labId);
    return lab?.lab_name || 'Inconnu';
  };

  const filteredAlerts = alerts.filter(alert => {
    if (statusFilter !== 'all' && alert.status !== statusFilter) return false;
    if (clientFilter !== 'all' && alert.client_id !== clientFilter) return false;
    return true;
  });

  const pendingCount = alerts.filter(a => a.status === 'pending').length;

  return (
    <AdminLayout 
      title="Alertes" 
      subtitle={`${pendingCount} alerte${pendingCount > 1 ? 's' : ''} en attente`}
    >
      <Card className="border-border" data-testid="alerts-page">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Alertes anti-désintermédiation
          </CardTitle>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-32" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="resolved">Résolues</SelectItem>
                <SelectItem value="dismissed">Ignorées</SelectItem>
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-40" data-testid="client-filter">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" />
            </div>
          ) : filteredAlerts.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-green-500 opacity-50" />
              <p className="text-lg font-medium mb-2">Aucune alerte</p>
              <p className="text-sm">
                {statusFilter === 'pending' 
                  ? 'Toutes les alertes ont été traitées.' 
                  : 'Aucune alerte ne correspond aux filtres.'}
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Sévérité</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Laboratoire</TableHead>
                    <TableHead>Détails</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAlerts.map((alert) => (
                    <TableRow 
                      key={alert.id} 
                      className={alert.status === 'pending' ? 'bg-yellow-50/50' : ''}
                      data-testid={`alert-row-${alert.id}`}
                    >
                      <TableCell>
                        <Badge 
                          variant={alert.severity === 'high' ? 'destructive' : 'secondary'}
                          className={alert.severity === 'high' ? 'risk-high' : 'risk-medium'}
                        >
                          {alert.severity === 'high' ? 'Élevé' : 'Moyen'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {getClientName(alert.client_id)}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {getLabName(alert.lab_id)}
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <p className="text-sm truncate" title={alert.risk_details}>
                          {alert.risk_details}
                        </p>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {new Date(alert.created_at).toLocaleString('fr-FR')}
                      </TableCell>
                      <TableCell>
                        {alert.status === 'pending' && (
                          <Badge variant="outline" className="status-pending">
                            <Clock className="h-3 w-3 mr-1" />
                            En attente
                          </Badge>
                        )}
                        {alert.status === 'resolved' && (
                          <Badge variant="outline" className="status-active">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Résolu
                          </Badge>
                        )}
                        {alert.status === 'dismissed' && (
                          <Badge variant="outline" className="status-inactive">
                            <XCircle className="h-3 w-3 mr-1" />
                            Ignoré
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {alert.status === 'pending' && (
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleUpdateStatus(alert.id, 'resolved')}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              data-testid={`resolve-alert-${alert.id}`}
                            >
                              <CheckCircle2 className="h-4 w-4 mr-1" />
                              Résoudre
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleUpdateStatus(alert.id, 'dismissed')}
                              className="text-muted-foreground"
                              data-testid={`dismiss-alert-${alert.id}`}
                            >
                              Ignorer
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{alerts.filter(a => a.status === 'pending').length}</p>
                <p className="text-sm text-muted-foreground">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{alerts.filter(a => a.status === 'resolved').length}</p>
                <p className="text-sm text-muted-foreground">Résolues</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{alerts.filter(a => a.severity === 'high').length}</p>
                <p className="text-sm text-muted-foreground">Sévérité élevée</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Alerts;
